import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { ERP_APPROVER_ROLES } from "@/lib/roles";
import { StepApproverSelect, useActiveUsers } from "@/components/masters/StepApproverSelect";

/**
 * Pencil button + edit dialog for a master record.
 * fields: [{ key, label, type?: "text"|"number"|"textarea"|"select", options?: [{value,label}], step?, min?, max?, hint? }]
 * onSave(values) -> Promise (do the PATCH); the dialog closes and onSaved() runs on success.
 */
export function EditMasterDialog({ title, fields, initial, onSave, onSaved, testId, note }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const openDialog = () => {
    const v = {};
    fields.forEach((f) => { v[f.key] = initial?.[f.key] ?? ""; });
    setForm(v);
    setErrors({});
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      const values = {};
      fields.forEach((f) => {
        const raw = form[f.key];
        values[f.key] = f.type === "number" ? (raw === "" ? null : Number(raw)) : (typeof raw === "string" ? raw.trim() : raw);
      });
      await onSave(values);
      toast.success("Saved");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button type="button" size="sm" variant="ghost" title="Edit" onClick={openDialog} data-testid={testId}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3.5" data-testid={`${testId}-form`}>
            {fields.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1.5" rows={3} />
                ) : f.type === "select" ? (
                  <Select value={String(form[f.key] ?? "")} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type === "number" ? "number" : "text"} step={f.step} min={f.min} max={f.max}
                    value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1.5" />
                )}
                {f.hint && <p className="mt-1 text-[11px] text-muted-foreground">{f.hint}</p>}
                <FieldError error={errors[f.key]} />
              </div>
            ))}
            {note && <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">{note}</p>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Edit an approval workflow: name + the ordered approval steps. */
export function EditWorkflowDialog({ workflow, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const users = useActiveUsers();

  const openDialog = () => {
    setName(workflow.name);
    setSteps((workflow.steps || []).slice().sort((a, b) => a.step_order - b.step_order).map((s) => ({ name: s.name, approver_role: s.approver_role, approver_user_id: s.approver_user_id || "" })));
    setOpen(true);
  };
  const updateStep = (idx, field, value) => setSteps(steps.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));

  const submit = async (e) => {
    e.preventDefault();
    if (steps.length === 0) { toast.error("A workflow needs at least one step"); return; }
    setSaving(true);
    try {
      await api.patch(`/workflows/${workflow.id}`, {
        name: name.trim(),
        steps: steps.map((s, idx) => ({ step_order: idx + 1, name: s.name.trim(), approver_role: s.approver_role, approver_user_id: s.approver_user_id || null })),
      });
      toast.success("Workflow saved");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button type="button" size="sm" variant="ghost" title="Edit workflow" onClick={openDialog} data-testid={`edit-workflow-button-${workflow.doc_type}`}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader><DialogTitle>Edit workflow — {workflow.doc_type}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3.5">
            <div><Label>Name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
            <div>
              <Label>Steps (in order)</Label>
              <div className="mt-1.5 space-y-2">
                {steps.map((s, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="w-5 shrink-0 font-mono text-xs text-muted-foreground">{idx + 1}.</span>
                    <Input required placeholder="Step name" value={s.name} onChange={(e) => updateStep(idx, "name", e.target.value)} />
                    <Select value={s.approver_role} onValueChange={(v) => updateStep(idx, "approver_role", v)}>
                      <SelectTrigger className="w-40 shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>{ERP_APPROVER_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                    <StepApproverSelect value={s.approver_user_id} users={users} onChange={(v) => updateStep(idx, "approver_user_id", v)} testId={`edit-workflow-step-person-select-${idx}`} />
                    {steps.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setSteps(steps.filter((_, i) => i !== idx))}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setSteps([...steps, { name: "", approver_role: ERP_APPROVER_ROLES[0], approver_user_id: "" }])}>
                <Plus className="h-3.5 w-3.5" /> Add step
              </Button>
            </div>
            <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">Documents already waiting for approval keep the steps they were submitted with.</p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
